import React, { useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { User as UserIcon, LogIn, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { usePortalI18n } from "./PortalI18nContext";
import PortalLanguageSwitcher from "./PortalLanguageSwitcher";

// AP-026 / AP-058 / AP-058B · Kreyòl Ayisyen messages for the Client Portal.
// NOTE: Backend authentication / validation logic is intentionally untouched.
// Backend now returns a machine-readable `code` in the 401 payload so the UI
// can pick the exact Kreyòl message.
const KREYOL = {
  SUCCESS: "Byenvini! W ap antre nan Portal Kliyan JMS la...",
  // Client-side (per-field) validation.
  NAME_REQUIRED: "Tanpri antre non konplè ou.",
  DATE_REQUIRED: "Tanpri antre dat nesans ou.",
  DATE_INVALID:  "Dat nesans lan pa nan bon fòma. Tanpri itilize fòma: JJ/MM/AAAA.",
  // Backend-mapped feedback (per code).
  NAME_NOT_FOUND:     "Non kliyan sa a pa anrejistre nan sistèm JMS la.",
  DATE_MISMATCH:      "Dat nesans sa a pa koresponn ak dosye kliyan sa a.",
  PORTAL_PENDING:     "Kont kliyan ou a poko aktive. Tanpri kontakte JMS Multi Service.",
  PORTAL_BLOCKED:     "Kont kliyan ou a bloke. Tanpri kontakte JMS Multi Service.",
  PORTAL_CLOSED:      "Kont kliyan ou a fèmen. Tanpri kontakte JMS Multi Service.",
  PORTAL_SUSPENDED:   "Kont kliyan ou a sispann. Tanpri kontakte JMS Multi Service.",
  GENERIC_ERROR:      "Enfòmasyon ou yo pa kòrèk. Tanpri verifye non konplè ou ak dat nesans ou.",
  // AP-042 · Full support message shown whenever the login attempt fails.
  SUPPORT_MESSAGE:
    "⚠️ Nou pa t kapab verifye enfòmasyon ou yo.\n" +
    "Tanpri verifye non konplè ou ak dat nesans ou epi eseye ankò.\n" +
    "Si pwoblèm nan kontinye, kontakte JMS MULTI SERVICE LTDA pou nou verifye enfòmasyon ki anrejistre sou dosye ou.",
  PORTAL_DISABLED:
    "Kont kliyan ou a poko aktive. Tanpri kontakte JMS Multi Service.",
  NO_MATCH:
    "Nou pa t kapab verifye enfòmasyon ou yo. Tanpri verifye non konplè ou ak dat nesans ou.",
  // AP-047 · Client Portal Login Security — 3 failed attempts / 30-minute block.
  ACCOUNT_LOCKED_TITLE: "🔒 Kont ou bloke tanporèman.",
  ACCOUNT_LOCKED_BODY:
    "Pou pwoteje kont ou, sistèm JMS la bloke koneksyon an apre 3 move tantativ.\n" +
    "Tanpri tann 30 minit anvan ou eseye ankò.\n" +
    "Si ou bezwen asistans, klike sou:\n" +
    "💬 Kontakte JMS sou WhatsApp",
};

// AP-120.3 · WhatsApp de suporte REMOVIDO do Login. Substituído pelo botão
// "🤖 Mwen bezwen asistans" que mostra instruções em Kreyòl para o cliente
// tentar novamente ou entrar em contato com a Secretaria pelo próprio Portal
// (Assistente IA após login).
function openAssistantInfo() {
  alert(
    "🤖 Asistan IA JMS\n\n" +
    "Konekte sou Portal Kliyan an ak non ou, dat nesans ou epi nimewo WhatsApp ou.\n\n" +
    "Aprè ou konekte, Asistan IA JMS a ap disponib pou reponn kesyon ou yo, tcheke dosye ou epi kreye tikè si sa nesesè.\n\n" +
    "Si ou pa gen aksè ankò, tanpri kontakte Sekretarya JMS."
  );
}

// AP-057 · Type the birth date manually in DD/MM/YYYY. No calendar / date
// picker. The backend keeps expecting ISO (YYYY-MM-DD) — we convert on submit.
const maskBirthDate = (raw) => {
  const d = (raw || "").replace(/\D+/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

// Returns YYYY-MM-DD when the masked value is a real calendar date, else "".
const brToIso = (brDate) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(brDate || "");
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  const day = Number(dd), month = Number(mm), year = Number(yyyy);
  if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return "";
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return "";
  return `${yyyy}-${mm}-${dd}`;
};

export default function PortalLogin() {
  const { t } = usePortalI18n();
  const { client, loading, login } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [fullName, setFullName] = useState("");
  const [birthDate, setBirthDate] = useState(""); // masked value DD/MM/YYYY
  const [submitting, setSubmitting] = useState(false);
  // AP-058 · Per-field errors. Empty string means "ok".
  const [nameError, setNameError] = useState("");
  const [dateError, setDateError] = useState("");
  // Form-level (backend) error banner.
  const [formError, setFormError] = useState({ level: "", text: "" });
  // Refs so we can focus the first invalid field.
  const nameRef = useRef(null);
  const dateRef = useRef(null);

  // If already logged in, bounce to the portal dashboard (or intended target).
  if (!loading && client) {
    const to = location.state?.from || "/portal";
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError({ level: "", text: "" });

    // ---- AP-058 · Per-field client-side validation ----
    const trimmedName = fullName.trim();
    let nameErr = "";
    let dateErr = "";
    if (!trimmedName) nameErr = KREYOL.NAME_REQUIRED;
    if (!birthDate.trim()) {
      dateErr = KREYOL.DATE_REQUIRED;
    } else if (!brToIso(birthDate)) {
      dateErr = KREYOL.DATE_INVALID;
    }
    setNameError(nameErr);
    setDateError(dateErr);
    if (nameErr || dateErr) {
      // Focus the FIRST field with an error (top-down: name → date).
      if (nameErr && nameRef.current) nameRef.current.focus();
      else if (dateErr && dateRef.current) dateRef.current.focus();
      toast.error(nameErr || dateErr, {
        icon: <XCircle className="h-5 w-5 text-red-600" />,
      });
      return;
    }

    setSubmitting(true);
    try {
      // AP-025 · WhatsApp removed from portal login. Pass empty string for
      // legacy compatibility with older PortalAuthContext signatures.
      // AP-057 · Convert typed DD/MM/YYYY → ISO YYYY-MM-DD so the backend
      // contract is unchanged.
      await login(trimmedName, brToIso(birthDate), "");
      toast.success(KREYOL.SUCCESS, {
        icon: <CheckCircle2 className="h-5 w-5 text-emerald-600" />,
      });
      const to = location.state?.from || "/portal";
      navigate(to, { replace: true });
    } catch (err) {
      // AP-058 · Map backend detail strings (Portuguese) to Kreyòl messages
      // without changing the API contract.
      const detail = err?.response?.data?.detail;
      const detailStr = typeof detail === "string" ? detail : "";
      const detailObj = (typeof detail === "object" && detail) ? detail : null;
      const code = detailObj?.code || "";
      const isPortalDisabled = detailStr.includes("ainda não foi liberado")
        || code === "PORTAL_DISABLED";
      // AP-047 · Account locked after 3 failed attempts.
      if (code === "ACCOUNT_LOCKED") {
        const mins = Number(detailObj?.minutes_remaining || 30);
        setFormError({
          level: "locked",
          text: `${KREYOL.ACCOUNT_LOCKED_TITLE}\n\n${KREYOL.ACCOUNT_LOCKED_BODY}`,
          minutes: mins,
        });
        toast.error(KREYOL.ACCOUNT_LOCKED_TITLE, {
          icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
        });
        return;
      }
      if (isPortalDisabled) {
        setFormError({ level: "warn", text: KREYOL.PORTAL_DISABLED });
        toast.warning(KREYOL.PORTAL_DISABLED, {
          icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
        });
      } else {
        // Any other 401 → "we didn't find this client". Preserve fields; hint
        // that both fields could be wrong (backend is intentionally opaque).
        setFormError({ level: "error", text: KREYOL.NO_MATCH });
        // Highlight both credential fields so the user knows exactly what to
        // review, and focus the top one.
        setNameError(" "); setDateError(" ");
        if (nameRef.current) nameRef.current.focus();
        toast.error(KREYOL.NO_MATCH, {
          icon: <XCircle className="h-5 w-5 text-red-600" />,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="portal-login-page" className="min-h-screen bg-muted/30 grid place-items-center px-4">
      <div className="w-full max-w-md">
        {/* AP-082 F2 · Seletor de idioma também na tela de login */}
        <div className="flex justify-end mb-2"><PortalLanguageSwitcher /></div>
        <div className="text-center mb-6">
          <div className="inline-flex h-14 w-14 rounded-2xl bg-primary/10 border border-primary/30 items-center justify-center mb-3">
            <UserIcon className="h-7 w-7 text-primary" strokeWidth={2.2} />
          </div>
          <h1 className="text-2xl font-display font-bold tracking-tight text-foreground">{t("login.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("login.subtitle")}
          </p>
        </div>

        <form
          data-testid="portal-login-form"
          onSubmit={onSubmit}
          className="rounded-2xl border border-border bg-white p-6 space-y-4 shadow-sm"
        >
          <div>
            <Label className="text-xs" htmlFor="portal-fullname">{t("login.fullName")}</Label>
            <Input
              id="portal-fullname"
              data-testid="portal-login-name"
              autoFocus
              ref={nameRef}
              className={`mt-1 ${nameError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              value={fullName}
              onChange={(e) => { setFullName(e.target.value); if (nameError) setNameError(""); }}
              placeholder={t("login.fullNamePh")}
              disabled={submitting}
              aria-invalid={!!nameError}
              aria-describedby={nameError ? "portal-name-error" : undefined}
            />
            {nameError && nameError.trim() !== "" && (
              <p id="portal-name-error" data-testid="portal-name-error"
                 className="text-[12px] text-red-600 mt-1">❌ {nameError}</p>
            )}
          </div>
          <div>
            <Label className="text-xs" htmlFor="portal-birthdate">{t("login.birthDate")}</Label>
            <Input
              id="portal-birthdate"
              data-testid="portal-login-birthdate"
              ref={dateRef}
              type="text"
              inputMode="numeric"
              autoComplete="bday"
              placeholder="dd/mm/aaaa"
              maxLength={10}
              className={`mt-1 ${dateError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              value={birthDate}
              onChange={(e) => { setBirthDate(maskBirthDate(e.target.value)); if (dateError) setDateError(""); }}
              disabled={submitting}
              aria-invalid={!!dateError}
              aria-describedby={dateError ? "portal-date-error" : undefined}
            />
            {dateError && dateError.trim() !== "" ? (
              <p id="portal-date-error" data-testid="portal-date-error"
                 className="text-[12px] text-red-600 mt-1">❌ {dateError}</p>
            ) : (
              <p className="text-[11px] text-muted-foreground mt-1">
                {t("login.birthHint")}
              </p>
            )}
          </div>

          {formError.text && (
            <div data-testid="portal-login-error-block" className="space-y-3">
              <div
                data-testid={formError.level === "locked" ? "portal-login-locked" : "portal-login-error"}
                className={
                  "text-sm rounded-md px-3 py-2 border whitespace-pre-line " +
                  (formError.level === "locked"
                    ? "text-rose-800 bg-rose-50 border-rose-300 font-medium"
                    : formError.level === "warn"
                      ? "text-amber-800 bg-amber-50 border-amber-200"
                      : "text-destructive bg-destructive/5 border-destructive/20")
                }
              >
                {formError.level === "locked" ? formError.text : KREYOL.SUPPORT_MESSAGE}
              </div>
              {/* AP-120.3 · WhatsApp REMOVIDO — Assistente IA é o canal oficial. */}
              <button
                type="button"
                data-testid="portal-login-assistant"
                onClick={openAssistantInfo}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-2.5 px-4 transition shadow-sm"
              >
                <span aria-hidden="true">🤖</span>
                Mwen bezwen asistans
              </button>
            </div>
          )}

          <Button
            data-testid="portal-login-submit"
            type="submit"
            disabled={submitting}
            className="w-full bg-primary hover:bg-primary/90"
          >
            <LogIn className="h-4 w-4 mr-2" />
            {submitting ? t("login.submitting") : t("login.submit")}
          </Button>

          <p className="text-[11px] text-center text-muted-foreground pt-1">
            {t("login.activateHelp")}
          </p>
        </form>
      </div>
    </div>
  );
}
